import { useState, useEffect, useMemo } from "react";
import "@/App.css";
import { HashRouter, Routes, Route, Link } from "react-router-dom";
import {
  company as staticCompany,
  stats as staticStats,
  services as staticServices,
  projects as staticProjects,
  gallery as staticGallery,
  leadership as staticLeadership,
  hsePillars as staticHsePillars
} from "@/data";
import { Wrench, Factory, HardHat, Shield, Zap, TriangleAlert as AlertTriangle, Leaf, Menu, X, MapPin, Phone, Mail, ChevronRight, Heart, Users, Settings, LayoutDashboard, FileText, Image as ImageIcon, MessageSquare, Building2, Calendar } from "lucide-react";

// Icon mapping
const iconMap = {
  Wrench, Factory, HardHat, Shield, Zap, AlertTriangle, Leaf, Heart, Users
};

// Helper to get icon component
const getIcon = (iconName, size = 32) => {
  const Icon = iconMap[iconName] || Wrench;
  return <Icon size={size} />;
};

// Default service images
const DEFAULT_SERVICE_IMAGES = {
  "Mechanical Construction": "https://images.unsplash.com/photo-1720036237098-9f21aea3e5ee?w=800",
  "Plant & Machinery Installation": "https://images.unsplash.com/photo-1709804250377-27ce2d57209e?w=800",
  "Civil Works": "https://images.unsplash.com/photo-1768406938495-b72600c52cbe?w=800",
  "Corrosion Protection": "https://images.unsplash.com/photo-1586057285471-2f78bffaf074?w=800",
  "Cathodic Protection": "https://images.unsplash.com/photo-1563456020978-5a5eb63ce380?w=800",
  "Emergency Pipeline Repair": "https://images.unsplash.com/photo-1612438137269-70c848e102e1?w=800",
  "Environmental Restoration": "https://images.unsplash.com/photo-1768759578182-b686cf94ddff?w=800"
};

// Service details data
const SERVICE_DETAILS = {
  "Mechanical Construction": {
    scope: "Land, Offshore, and Swamp environments",
    fullDescription: "CHOZE Ventures provides comprehensive mechanical construction services for oil and gas facilities. Our expertise covers the complete spectrum of process pipe work, pipelines, and flow lines installation across land, offshore, and swamp environments.",
    capabilities: ["Oil and Gas process pipe work and facilities (Land, Offshore, Swamp)", "Pipelines/Flowlines pipe-work", "Pipelines Pigging, Hydro-test & Drying Services", "Fire delivery water systems", "Pump Houses and Metering Stations"],
    relatedProjects: ["Construction of Saver Pit at Amukpe Flow Station", "Completed Fire Hydrant Line", "Site showing work ongoing of Fit Up-12\" Tie in Joints"]
  },
  "Plant & Machinery Installation": {
    scope: "Industrial facilities and water systems",
    fullDescription: "Expert installation services for pumps, metering stations, wellhead platforms, and water treatment facilities.",
    capabilities: ["Pump Houses and Metering Stations installation", "Wellhead Platform and Hook-up (Land, Swamp & Offshore)", "Water Projects (Borehole Drilling and Distribution)", "Water treatment Facilities installation", "Equipment commissioning and testing"],
    relatedProjects: ["Procurement and Installation of LTF Mono Pumps Carport Canopy", "Completed Carport Canopy Shed for Booster Pump at Amukpe Flow Station"]
  },
  "Civil Works": {
    scope: "Construction and infrastructure development",
    fullDescription: "Civil engineering services including excavation, concrete works, and building construction.",
    capabilities: ["Excavation and earthworks", "Miscellaneous concrete works", "Erection of Buildings", "Civil maintenance works", "Infrastructure development"],
    relatedProjects: ["Edjeba Mini Basket Ball Court", "Upgrade of Forcados Kitchen, Restaurant, Recreation, Office and Ancillary Facilities"]
  },
  "Corrosion Protection": {
    scope: "Steel structures and storage facilities",
    fullDescription: "Advanced corrosion protection services including sand/grit blasting and application of protective coating for steel structures, pipes, fittings, and storage tanks.",
    capabilities: ["Sand/Grit blasting operations", "Application of protective coating", "Steel structures treatment", "Pipes and fittings protection", "Storage tank coating and protection"],
    relatedProjects: ["Painting, Decoration and External Works", "Steel structure maintenance projects"]
  },
  "Cathodic Protection": {
    scope: "Pipeline and facility protection systems",
    fullDescription: "Complete cathodic protection services from engineering design through materials procurement, installation, testing, commissioning, and ongoing maintenance.",
    capabilities: ["Engineering Design for CP systems", "Materials procurement", "Installation of CP systems", "Testing and Commissioning", "Ongoing Maintenance services"],
    relatedProjects: ["Provision of Ultrasonic Test, Leak Detection Test and Calibration Services"]
  },
  "Emergency Pipeline Repair": {
    scope: "Rapid response repair services",
    fullDescription: "Rapid response emergency repair services for pipeline systems including sectional replacement, clamping, sleeving, and hot tapping operations.",
    capabilities: ["Sectional replacement of damaged pipeline", "Clamping operations", "Sleeving repairs", "Hot tapping services", "Emergency response and mobilization"],
    relatedProjects: ["Fabrication and Installation of 3\" Fire Hydrant at Amukpe Station", "Dismantling of 6m Citadel Gate and Installation of 12m Citadel Gate"]
  },
  "Environmental Restoration": {
    scope: "Oil spill cleanup and remediation",
    fullDescription: "Environmental cleanup and remediation services for oil spill affected areas. We specialize in oil spill clean up, soil remediation, and restoration of impacted environments.",
    capabilities: ["Oil Spill clean up operations", "Soil remediation", "Water body restoration", "Environmental impact assessment", "Community environmental restoration"],
    relatedProjects: ["Provision of Spill/Remediation Services", "Recovery/cleanup of Okpe spill impacted communities river waters", "Cleanup/Remediation of Impacted Spill at Iriama", "Provision of Remediation Services at Imo River Package 14"]
  }
};

// Client badge class helper
const getClientBadgeClass = (client) => {
  const classes = {
    "SPDC": "badge-spdc",
    "SEPLAT": "badge-seplat",
    "SNEPCO": "badge-snepco",
    "HEOSL": "badge-heosl",
    "GLADTRICO": "badge-gladtrico"
  };
  return classes[client] || "badge-spdc";
};

// Navigation Component
const Navigation = ({ isAdmin = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = useMemo(() => isAdmin 
    ? [{ href: "/", label: "View Site" }]
    : [
        { href: "#about", label: "About" },
        { href: "#services", label: "Services" },
        { href: "#projects", label: "Projects" },
        { href: "#gallery", label: "Gallery" },
        { href: "#leadership", label: "Leadership" },
        { href: "#contact", label: "Contact" }
      ], [isAdmin]);

  return (
    <nav data-testid="main-navigation" className={`fixed top-0 left-0 right-0 z-50 px-[5%] py-4 glass-nav border-b border-[#1e293b] transition-all ${scrolled ? "py-3" : ""}`}>
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 bg-[#00b4d8] clip-hexagon flex items-center justify-center">
            <span className="font-['Bebas_Neue'] text-xl text-[#0a0f1e] font-bold">CV</span>
          </div>
          <div>
            <span className="font-['Bebas_Neue'] text-lg tracking-widest text-white block">CHOZE VENTURES</span>
            <span className="text-[0.6rem] tracking-[0.2em] text-[#00b4d8] uppercase -mt-1 block">& Company Ltd.</span>
          </div>
        </Link>

        <ul className="hidden md:flex gap-10 list-none">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a href={link.href} className="font-['Barlow_Condensed'] text-sm font-semibold tracking-[0.15em] uppercase text-[#94a3b8] hover:text-[#00b4d8] transition-colors" data-testid={`nav-link-${link.label.toLowerCase()}`}>
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-4">
          {!isAdmin && (
            <Link to="/admin" className="hidden md:block font-['Barlow_Condensed'] text-sm font-bold tracking-[0.15em] uppercase text-[#0a0f1e] bg-[#00b4d8] px-5 py-2 clip-btn-nav hover:bg-white transition-colors" data-testid="admin-cta-button">
              Admin Portal
            </Link>
          )}
          <button className="md:hidden text-[#00b4d8]" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-toggle">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden mt-4 pb-4 border-t border-[#1e293b] pt-4" data-testid="mobile-menu">
          <ul className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a href={link.href} className="font-['Barlow_Condensed'] text-sm font-semibold tracking-[0.15em] uppercase text-[#94a3b8] hover:text-[#00b4d8]" onClick={() => setIsOpen(false)}>
                  {link.label}
                </a>
              </li>
            ))}
            {!isAdmin && (
              <li><Link to="/admin" className="font-['Barlow_Condensed'] text-sm font-bold text-[#00b4d8]">Admin Portal</Link></li>
            )}
          </ul>
        </div>
      )}
    </nav>
  );
};

// Section Label Component
const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-px bg-[#00b4d8]" />
    <span className="font-['Barlow_Condensed'] text-xs font-semibold tracking-[0.3em] uppercase text-[#00b4d8]">{text}</span>
  </div>
);

// Hero Section
const HeroSection = ({ company, stats }) => (
  <section id="hero" data-testid="hero-section" className="min-h-screen flex flex-col justify-end px-[5%] pb-32 pt-24 relative overflow-hidden bg-gradient-to-br from-[#0a0f1e] to-[#060910]">
    <div className="absolute inset-0 hero-grid-bg animate-grid-move" />
    <div className="absolute right-[-10%] top-[10%] w-[700px] h-[700px] rounded-full border border-[rgba(0,180,216,0.1)] shadow-[inset_0_0_120px_rgba(0,180,216,0.05),0_0_120px_rgba(0,180,216,0.05)]">
      <div className="absolute inset-10 rounded-full border border-[rgba(0,180,216,0.08)]" />
      <div className="absolute inset-20 rounded-full border border-[rgba(0,180,216,0.05)]" />
    </div>
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1571524522669-99d0c9e7264d?w=1920&q=80)` }} />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1e] via-[#0a0f1e]/70 to-[#0a0f1e]/40" />

    <div className="relative z-10">
      <div className="inline-flex items-center gap-2 border border-[#1e293b] px-4 py-2 mb-6 animate-fade-up-delay-1" data-testid="hero-badge">
        <span className="w-2 h-2 bg-[#00b4d8] rounded-full animate-pulse-slow" />
        <span className="font-['Barlow_Condensed'] text-xs tracking-[0.2em] text-[#00b4d8] uppercase">Established {company?.established_year || 1997}</span>
      </div>
      <h1 className="font-['Bebas_Neue'] text-[clamp(3rem,10vw,8rem)] leading-[0.9] text-white animate-fade-up-delay-2" data-testid="hero-title">
        ENGINEERING<br/><em className="text-[#00b4d8] not-italic">EXCELLENCE</em>
      </h1>
      <p className="max-w-xl mt-6 text-lg leading-relaxed text-[#94a3b8] font-light animate-fade-up-delay-3" data-testid="hero-description">
        {company?.tagline || "Delivering world-class Oil & Gas engineering, environmental restoration and consultancy services across Nigeria since 1997."}
      </p>
      <div className="flex gap-4 mt-8 animate-fade-up-delay-4">
        <a href="#services" className="font-['Barlow_Condensed'] text-base font-bold tracking-[0.15em] uppercase text-[#0a0f1e] bg-[#00b4d8] px-8 py-4 clip-btn-primary hover:bg-white transition-all hover:-translate-y-1" data-testid="hero-cta-services">Our Services</a>
        <a href="#contact" className="font-['Barlow_Condensed'] text-base font-bold tracking-[0.15em] uppercase text-[#00b4d8] border border-[#1e293b] px-8 py-4 hover:border-[#00b4d8] hover:bg-[rgba(0,180,216,0.15)] transition-all" data-testid="hero-cta-contact">Get In Touch</a>
      </div>
    </div>

    {stats && stats.length > 0 && (
      <div className="absolute bottom-32 right-[5%] hidden lg:flex flex-col gap-6 animate-fade-up-delay-4" data-testid="hero-stats">
        {stats.map((stat) => (
          <div key={stat.id} className="text-right">
            <span className="font-['Bebas_Neue'] text-4xl text-[#00b4d8] leading-none block">{stat.value}</span>
            <span className="text-xs tracking-[0.2em] uppercase text-[#94a3b8]">{stat.label}</span>
          </div>
        ))}
      </div>
    )}
  </section>
);

// Clients Ticker
const ClientsTicker = () => {
  const clients = ["SHELL SPDC", "SNEPCO", "SEPLAT", "HEOSL", "GLADTRICO", "SHELL SPDC", "SNEPCO"];
  return (
    <div className="bg-[#12182b] border-y border-[#1e293b] py-4 overflow-hidden" data-testid="clients-ticker">
      <div className="flex animate-ticker w-max">
        {[0, 1].map((i) => (
          <div key={`ticker-${i}`} className="flex">
            {clients.map((client, idx) => (
              <span key={`${i}-${client}-${idx}`} className="px-12 font-['Barlow_Condensed'] text-sm font-semibold tracking-[0.25em] uppercase text-[#94a3b8] whitespace-nowrap">
                Trusted by <strong className="text-[#00b4d8]">{client}</strong>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// About Section
const AboutSection = ({ company }) => {
  const features = [
    { icon: Building2, title: "Indigenous Company", desc: "Proudly Nigerian, serving the Oil & Gas sector" },
    { icon: Calendar, title: "Decades of Experience", desc: `Operating since ${company?.established_year || 1997}` },
    { icon: Shield, title: "HSE Commitment", desc: "Zero tolerance for unsafe acts and conditions" }
  ];

  return (
    <section id="about" className="py-28 px-[5%] bg-[#080c18]" data-testid="about-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
        <div>
          <SectionLabel text="About Us" />
          <h2 className="font-['Bebas_Neue'] text-[clamp(2.5rem,5vw,4rem)] leading-[0.95] text-white mb-6">{company?.name || "CHOZE VENTURES & COMPANY LTD."}</h2>
          <p className="text-base leading-relaxed text-[#94a3b8] font-light mb-8">{company?.description || "Loading..."}</p>
          <div className="flex flex-col gap-4">
            {features.map((feature, idx) => (
              <div key={feature.title} className="flex gap-4 items-start p-5 border border-[#1e293b] bg-[rgba(0,180,216,0.03)] feature-hover" data-testid={`about-feature-${idx}`}>
                <div className="w-10 h-10 flex-shrink-0 bg-[rgba(0,180,216,0.15)] border border-[#1e293b] flex items-center justify-center text-[#00b4d8]">
                  <feature.icon size={20} />
                </div>
                <div>
                  <strong className="block text-sm font-semibold text-white mb-1">{feature.title}</strong>
                  <span className="text-sm text-[#94a3b8] leading-relaxed">{feature.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="relative aspect-[4/5] bg-gradient-to-br from-[#12182b] to-[#0d1a2e] border border-[#1e293b] flex items-center justify-center overflow-hidden">
            <div className="absolute top-[-1px] left-[-1px] w-2/5 h-[2px] bg-[#00b4d8]" />
            <div className="absolute bottom-[-1px] right-[-1px] w-2/5 h-[2px] bg-[#00b4d8]" />
            <img src="https://images.unsplash.com/photo-1720036237098-9f21aea3e5ee?w=600" alt="Industrial facility" className="w-full h-full object-cover opacity-60" />
          </div>
          <div className="absolute bottom-[-2rem] left-[-2rem] bg-[#00b4d8] text-[#0a0f1e] p-6 text-center" data-testid="established-badge">
            <strong className="font-['Bebas_Neue'] text-4xl block leading-none">{company?.established_year || 1997}</strong>
            <span className="text-xs tracking-[0.2em] uppercase font-bold">Established</span>
          </div>
          <div className="absolute top-[-1.5rem] right-[-1.5rem] w-24 h-24 bg-[#0a0f1e] border border-[#1e293b] rounded-full flex flex-col items-center justify-center text-center p-2" data-testid="cert-badge">
            <span className="text-[0.55rem] tracking-[0.1em] text-[#00b4d8] font-semibold uppercase leading-tight">{company?.rc_number || "RC 1451945"}<br/>Certified</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Service Card Component
const ServiceCard = ({ service, idx, onClick }) => {
  const isFeatured = idx < 2;
  const colSpan = idx === 0 ? "md:col-span-8" : idx === 1 ? "md:col-span-4" : "md:col-span-4";
  const rowSpan = idx < 2 ? "md:row-span-2" : "";
  const imageUrl = service.image_url || DEFAULT_SERVICE_IMAGES[service.title] || "";

  return (
    <div 
      className={`bg-[#12182b] border border-[#1e293b] card-hover cursor-pointer ${colSpan} ${rowSpan}`}
      data-testid={`service-card-${idx}`}
      onClick={onClick}
    >
      <div className={`w-full ${isFeatured ? "aspect-[16/9]" : "aspect-[16/10]"} bg-gradient-to-br from-[#0d1a2e] to-[#06111e] relative overflow-hidden`}>
        <span className="absolute top-4 left-4 font-['Bebas_Neue'] text-6xl text-[rgba(0,180,216,0.15)] z-10">0{idx + 1}</span>
        {imageUrl && <img src={imageUrl} alt={service.title} className="absolute inset-0 w-full h-full object-cover opacity-50" />}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-[#00b4d8] opacity-80 filter drop-shadow-[0_0_30px_rgba(0,180,216,0.6)] bg-[#0a0f1e]/60 p-4 rounded-full">
            {getIcon(service.icon)}
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#12182b] via-[#12182b]/50 to-transparent" />
      </div>
      <div className="p-6">
        <h3 className="font-['Barlow_Condensed'] text-xl font-bold tracking-wide text-white uppercase mb-3">{service.title}</h3>
        <p className="text-sm leading-relaxed text-[#94a3b8] mb-4">{service.description}</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {service.items.slice(0, 3).map((item) => (
            <span key={item} className="text-[0.65rem] tracking-[0.1em] uppercase text-[#00b4d8] bg-[rgba(0,180,216,0.15)] border border-[#1e293b] px-2 py-1">{item}</span>
          ))}
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-[#1e293b] font-['Barlow_Condensed'] text-xs tracking-[0.15em] uppercase text-[#00b4d8]">
          <span>Learn More</span>
          <ChevronRight size={16} />
        </div>
      </div>
    </div>
  );
};

// Service Detail Modal
const ServiceDetailModal = ({ service, onClose }) => {
  if (!service) return null;

  const details = SERVICE_DETAILS[service.title] || {};
  const defaultImgs = {
    "Mechanical Construction": ["https://images.unsplash.com/photo-1720036237098-9f21aea3e5ee?w=400", "https://images.unsplash.com/photo-1670690412533-e2b98fa71051?w=400"],
    "Plant & Machinery Installation": ["https://images.unsplash.com/photo-1709804250377-27ce2d57209e?w=400", "https://images.unsplash.com/photo-1720670996646-2f5d69a10ee7?w=400"],
    "Civil Works": ["https://images.unsplash.com/photo-1768406938495-b72600c52cbe?w=400", "https://images.unsplash.com/photo-1765972280045-7d0e47b376e3?w=400"],
    "Corrosion Protection": ["https://images.unsplash.com/photo-1586057285471-2f78bffaf074?w=400", "https://images.unsplash.com/photo-1675551136071-3c8937e6b603?w=400"],
    "Cathodic Protection": ["https://images.unsplash.com/photo-1563456020978-5a5eb63ce380?w=400", "https://images.unsplash.com/photo-1583438900036-80d1b7b1b9d3?w=400"],
    "Emergency Pipeline Repair": ["https://images.unsplash.com/photo-1612438137269-70c848e102e1?w=400", "https://images.unsplash.com/photo-1673423601254-776e7a40afe0?w=400"],
    "Environmental Restoration": ["https://images.unsplash.com/photo-1768759578182-b686cf94ddff?w=400", "https://images.unsplash.com/photo-1760624876201-be9e17d332fd?w=400"]
  };
  const projectImages = defaultImgs[service.title] || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0a0f1e]/90 backdrop-blur-sm" onClick={onClose} data-testid="service-detail-modal">
      <div className="bg-[#12182b] border border-[#1e293b] max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative h-48 md:h-64 overflow-hidden">
          <img src={service.image_url || DEFAULT_SERVICE_IMAGES[service.title]} alt={service.title} className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#12182b] via-[#12182b]/60 to-transparent" />
          <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 bg-[#0a0f1e]/80 border border-[#1e293b] flex items-center justify-center text-[#00b4d8] hover:bg-[#00b4d8] hover:text-[#0a0f1e] transition-colors" data-testid="close-modal-button">
            <X size={20} />
          </button>
          <div className="absolute bottom-6 left-6 flex items-center gap-4">
            <div className="w-14 h-14 bg-[#0a0f1e]/80 border border-[#00b4d8] flex items-center justify-center text-[#00b4d8]">{getIcon(service.icon, 40)}</div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#00b4d8] mb-1">{details.scope || "Oil & Gas Services"}</p>
              <h2 className="font-['Bebas_Neue'] text-3xl md:text-4xl text-white">{service.title}</h2>
            </div>
          </div>
        </div>
        <div className="p-6 md:p-8">
          <div className="mb-8">
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold tracking-wide text-[#00b4d8] uppercase mb-3">Overview</h3>
            <p className="text-base leading-relaxed text-[#94a3b8]">{details.fullDescription || service.description}</p>
          </div>
          <div className="mb-8">
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold tracking-wide text-[#00b4d8] uppercase mb-4">Our Capabilities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(details.capabilities || service.items).map((item) => (
                <div key={item} className="flex items-start gap-3 p-3 bg-[#0a0f1e] border border-[#1e293b]">
                  <ChevronRight size={16} className="text-[#00b4d8] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-[#94a3b8]">{item}</span>
                </div>
              ))}
            </div>
          </div>
          {details.relatedProjects?.length > 0 && (
            <div className="mb-8">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold tracking-wide text-[#00b4d8] uppercase mb-4">Related Projects</h3>
              <ul className="space-y-2">
                {details.relatedProjects.map((project) => (
                  <li key={project} className="flex items-center gap-2 text-sm text-[#94a3b8]">
                    <span className="w-1.5 h-1.5 bg-[#00b4d8] rounded-full" />{project}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold tracking-wide text-[#00b4d8] uppercase">Project Images</h3>
              <span className="text-xs text-[#94a3b8] italic">Admin can add actual project photos</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {projectImages.map((img, idx) => (
                <div key={`proj-img-${idx}`} className="aspect-video bg-[#0a0f1e] border border-[#1e293b] overflow-hidden relative group">
                  <img src={img} alt={`${service.title} project ${idx + 1}`} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
                </div>
              ))}
              <div className="aspect-video bg-[#0a0f1e] border border-dashed border-[#1e293b] flex flex-col items-center justify-center gap-2 text-[#94a3b8] hover:border-[#00b4d8] hover:text-[#00b4d8] transition-colors cursor-pointer">
                <ImageIcon size={24} /><span className="text-xs">Add Photo</span>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[#1e293b] flex flex-col md:flex-row gap-4 justify-between items-center">
            <p className="text-sm text-[#94a3b8]">Interested in our {service.title.toLowerCase()} services?</p>
            <a href="#contact" onClick={onClose} className="font-['Barlow_Condensed'] text-sm font-bold tracking-[0.15em] uppercase text-[#0a0f1e] bg-[#00b4d8] px-6 py-3 hover:bg-white transition-colors" data-testid="service-contact-cta">Request a Quote</a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Services Section
const ServicesSection = ({ services }) => {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <section id="services" className="py-28 px-[5%] bg-[#0a0f1e]" data-testid="services-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end mb-16">
        <div>
          <SectionLabel text="What We Do" />
          <h2 className="font-['Bebas_Neue'] text-[clamp(2.5rem,5vw,4rem)] leading-[0.95] text-white">COMPREHENSIVE<br/>SERVICES</h2>
        </div>
        <p className="text-base leading-relaxed text-[#94a3b8] font-light">From mechanical construction to environmental restoration, we deliver end-to-end solutions for the Oil and Gas industry with unwavering commitment to quality and safety.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {services.map((service, idx) => (
          <ServiceCard key={service.id} service={service} idx={idx} onClick={() => setSelectedService({...service, image_url: service.image_url || DEFAULT_SERVICE_IMAGES[service.title]})} />
        ))}
      </div>
      {selectedService && <ServiceDetailModal service={selectedService} onClose={() => setSelectedService(null)} />}
    </section>
  );
};

// Projects Section
const ProjectsSection = ({ projects }) => (
  <section id="projects" className="py-28 px-[5%] bg-[#080c18]" data-testid="projects-section">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-end mb-12">
      <div>
        <SectionLabel text="Track Record" />
        <h2 className="font-['Bebas_Neue'] text-[clamp(2.5rem,5vw,4rem)] leading-[0.95] text-white">PROJECTS<br/>EXECUTED</h2>
      </div>
      <p className="text-base leading-relaxed text-[#94a3b8] font-light">A proven track record of delivering complex engineering projects for major Oil and Gas companies across Nigeria.</p>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full border-collapse project-table" data-testid="projects-table">
        <thead>
          <tr>
            <th className="font-['Barlow_Condensed'] text-xs font-bold tracking-[0.2em] uppercase text-[#00b4d8] p-3 text-left border-b border-[#1e293b] bg-[rgba(0,180,216,0.05)]">Project Title</th>
            <th className="font-['Barlow_Condensed'] text-xs font-bold tracking-[0.2em] uppercase text-[#00b4d8] p-3 text-left border-b border-[#1e293b] bg-[rgba(0,180,216,0.05)]">Client</th>
            <th className="font-['Barlow_Condensed'] text-xs font-bold tracking-[0.2em] uppercase text-[#00b4d8] p-3 text-left border-b border-[#1e293b] bg-[rgba(0,180,216,0.05)] hidden md:table-cell">PO Number</th>
            <th className="font-['Barlow_Condensed'] text-xs font-bold tracking-[0.2em] uppercase text-[#00b4d8] p-3 text-left border-b border-[#1e293b] bg-[rgba(0,180,216,0.05)]">Year</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <tr key={project.id} className="transition-colors">
              <td className="p-3 text-sm text-white font-medium border-b border-[rgba(0,180,216,0.07)]">{project.title}</td>
              <td className="p-3 border-b border-[rgba(0,180,216,0.07)]">
                <span className={`font-['Barlow_Condensed'] text-xs font-bold tracking-[0.1em] uppercase px-2 py-1 ${getClientBadgeClass(project.client)}`}>{project.client}</span>
              </td>
              <td className="p-3 text-sm text-[#94a3b8] border-b border-[rgba(0,180,216,0.07)] hidden md:table-cell">{project.po_number}</td>
              <td className="p-3 text-sm text-[#94a3b8] border-b border-[rgba(0,180,216,0.07)]">{project.date_awarded}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

// Gallery Section
const GallerySection = ({ gallery }) => (
  <section id="gallery" className="py-28 px-[5%] bg-[#0a0f1e]" data-testid="gallery-section">
    <SectionLabel text="Our Work" />
    <h2 className="font-['Bebas_Neue'] text-[clamp(2.5rem,5vw,4rem)] leading-[0.95] text-white mb-12">PROJECT GALLERY</h2>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
      {gallery.map((item, idx) => {
        const isWide = idx === 0 || idx === 3;
        return (
          <div key={item.id} className={`gallery-item bg-[#12182b] border border-[#1e293b] relative overflow-hidden cursor-pointer ${isWide ? "col-span-2" : ""}`} style={{ aspectRatio: isWide ? "2/1" : "1/1" }} data-testid={`gallery-item-${idx}`}>
            <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
            <div className="gallery-overlay absolute inset-0 bg-gradient-to-t from-[rgba(0,180,216,0.8)] to-transparent" />
            <div className="gallery-content absolute bottom-0 left-0 right-0 p-4">
              <p className="text-xs font-semibold text-[#0a0f1e] font-['Barlow_Condensed'] tracking-[0.1em] uppercase">{item.title}</p>
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

// Leadership Section
const LeadershipSection = ({ leadership, hsePillars }) => (
  <section id="leadership" className="py-28 px-[5%] bg-[#080c18]" data-testid="leadership-section">
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-20 items-start">
      {leadership[0] && (
        <div className="bg-[#12182b] border border-[#1e293b] relative" data-testid="leader-card">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#00b4d8] to-transparent" />
          <div className="p-8">
            <h3 className="font-['Bebas_Neue'] text-3xl text-white tracking-wide">{leadership[0].name}</h3>
            <span className="text-xs tracking-[0.2em] uppercase text-[#00b4d8] font-semibold">{leadership[0].title}</span>
          </div>
          <div className="absolute top-[-20px] right-[-20px] w-16 h-16 bg-[#00b4d8] clip-hexagon flex items-center justify-center">
            <span className="font-['Bebas_Neue'] text-sm text-[#0a0f1e]">MD</span>
          </div>
        </div>
      )}
      <div>
        <SectionLabel text="Leadership" />
        <h2 className="font-['Bebas_Neue'] text-[clamp(2.5rem,5vw,4rem)] leading-[0.95] text-white mb-6">LEADERSHIP &<br/>HSE COMMITMENT</h2>
        <p className="text-base leading-relaxed text-[#94a3b8] font-light mb-8">{leadership[0]?.bio || "Our leadership team brings decades of experience in the Oil and Gas industry."}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {hsePillars.map((pillar, idx) => (
            <div key={pillar.id} className="p-5 border border-[#1e293b] bg-[rgba(0,180,216,0.03)] hover:border-[#00b4d8] transition-colors" data-testid={`hse-pillar-${idx}`}>
              {getIcon(pillar.icon, 24)}
              <h4 className="text-sm font-semibold text-white mb-1 mt-3">{pillar.title}</h4>
              <p className="text-xs text-[#94a3b8] leading-relaxed">{pillar.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Contact Section
const ContactSection = ({ company }) => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 400));
    setSubmitted(true);
    setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    setSubmitting(false);
  };

  const handleChange = (field) => (e) => setFormData(prev => ({...prev, [field]: e.target.value}));

  return (
    <section id="contact" className="py-28 px-[5%] bg-[#0a0f1e]" data-testid="contact-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
        <div>
          <SectionLabel text="Get In Touch" />
          <h2 className="font-['Bebas_Neue'] text-[clamp(2.5rem,5vw,4rem)] leading-[0.95] text-white mb-8">CONTACT US</h2>
          <div className="flex flex-col gap-8">
            <ContactItem icon={MapPin} label="Office Address" content={company?.addresses?.map((addr, i) => <p key={`addr-${i}`} className="text-sm text-[#94a3b8] leading-relaxed mb-2">{addr}</p>)} />
            <ContactItem icon={Phone} label="Phone Numbers" content={company?.phones?.map((phone, i) => <p key={`phone-${i}`} className="text-sm text-[#94a3b8]">{phone}</p>)} />
            <ContactItem icon={Mail} label="Email" content={<p className="text-sm text-[#94a3b8]">{company?.email || "info@chozeventures.com"}</p>} />
          </div>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="contact-form">
          {submitted ? (
            <div className="bg-[rgba(0,180,216,0.15)] border border-[#00b4d8] p-6 text-center">
              <h3 className="font-['Bebas_Neue'] text-2xl text-[#00b4d8] mb-2">MESSAGE SENT!</h3>
              <p className="text-sm text-[#94a3b8]">We'll get back to you shortly.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Full Name" type="text" value={formData.name} onChange={handleChange("name")} required testId="contact-input-name" />
                <FormField label="Email Address" type="email" value={formData.email} onChange={handleChange("email")} required testId="contact-input-email" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Phone Number" type="tel" value={formData.phone} onChange={handleChange("phone")} testId="contact-input-phone" />
                <FormField label="Subject" type="text" value={formData.subject} onChange={handleChange("subject")} required testId="contact-input-subject" />
              </div>
              <FormField label="Message" type="textarea" rows={5} value={formData.message} onChange={handleChange("message")} required testId="contact-input-message" />
              <button type="submit" disabled={submitting} className="font-['Barlow_Condensed'] text-base font-bold tracking-[0.15em] uppercase text-[#0a0f1e] bg-[#00b4d8] px-8 py-4 clip-btn-primary hover:bg-white transition-all disabled:opacity-50 self-start" data-testid="contact-submit-button">
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </>
          )}
        </form>
      </div>
    </section>
  );
};

// Contact Item Helper
const ContactItem = ({ icon: Icon, label, content }) => (
  <div className="flex gap-5 items-start">
    <div className="w-11 h-11 flex-shrink-0 bg-[rgba(0,180,216,0.15)] border border-[#1e293b] flex items-center justify-center text-[#00b4d8]">
      <Icon size={20} />
    </div>
    <div>
      <strong className="block text-xs tracking-[0.2em] uppercase text-[#00b4d8] mb-2">{label}</strong>
      {content}
    </div>
  </div>
);

// Form Field Helper
const FormField = ({ label, type, value, onChange, required, testId, rows }) => (
  <div className="flex flex-col gap-2">
    <label className="text-xs tracking-[0.2em] uppercase text-[#00b4d8]">{label}</label>
    {type === "textarea" ? (
      <textarea rows={rows} required={required} value={value} onChange={onChange} data-testid={testId} />
    ) : (
      <input type={type} required={required} value={value} onChange={onChange} data-testid={testId} />
    )}
  </div>
);

// Footer
const Footer = ({ company }) => (
  <footer className="py-12 px-[5%] bg-[#080c18] border-t border-[#1e293b]" data-testid="footer">
    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-[#00b4d8] clip-hexagon flex items-center justify-center">
          <span className="font-['Bebas_Neue'] text-sm text-[#0a0f1e] font-bold">CV</span>
        </div>
        <span className="font-['Bebas_Neue'] text-sm tracking-widest text-white">CHOZE VENTURES</span>
      </div>
      <p className="text-xs text-[#94a3b8]">&copy; {new Date().getFullYear()} {company?.name || "CHOZE VENTURES & COMPANY LTD."}. All rights reserved.</p>
    </div>
  </footer>
);

// Main Home Page
const Home = () => {
  const company = staticCompany;
  const stats = staticStats;
  const services = staticServices;
  const projects = staticProjects;
  const gallery = staticGallery;
  const leadership = staticLeadership;
  const hsePillars = staticHsePillars;

  return (
    <div>
      <Navigation />
      <HeroSection company={company} stats={stats} />
      <ClientsTicker />
      <AboutSection company={company} />
      <ServicesSection services={services || []} />
      <ProjectsSection projects={projects || []} />
      <GallerySection gallery={gallery || []} />
      <LeadershipSection leadership={leadership || []} hsePillars={hsePillars || []} />
      <ContactSection company={company} />
      <Footer company={company} />
    </div>
  );
};

// Admin Components
const AdminStatCard = ({ icon: Icon, label, value, idx }) => (
  <div className="admin-card p-6" data-testid={`admin-stat-${idx}`}>
    <Icon className="text-[#00b4d8] mb-3" size={24} />
    <p className="font-['Bebas_Neue'] text-4xl text-white">{value}</p>
    <p className="text-sm text-[#94a3b8]">{label}</p>
  </div>
);

const CompanyEditor = ({ company, onUpdate }) => {
  const [formData, setFormData] = useState(company || {});
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 300));
    if (onUpdate) onUpdate(formData);
    setSaving(false);
  };

  const handleChange = (field) => (e) => setFormData(prev => ({...prev, [field]: e.target.type === 'number' ? parseInt(e.target.value) : e.target.value}));

  return (
    <div className="admin-card p-6" data-testid="company-editor">
      <h2 className="font-['Bebas_Neue'] text-2xl text-white mb-6">Edit Company Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <FormField label="Company Name" type="text" value={formData.name || ""} onChange={handleChange("name")} testId="company-input-name" />
        <FormField label="Tagline" type="text" value={formData.tagline || ""} onChange={handleChange("tagline")} testId="company-input-tagline" />
      </div>
      <FormField label="Description" type="textarea" rows={4} value={formData.description || ""} onChange={handleChange("description")} testId="company-input-description" />
      <div className="mt-4">
        <FormField label="Mission Statement" type="textarea" rows={3} value={formData.mission || ""} onChange={handleChange("mission")} testId="company-input-mission" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-4">
        <FormField label="Established Year" type="number" value={formData.established_year || ""} onChange={handleChange("established_year")} testId="company-input-established" />
        <FormField label="Incorporation Year" type="number" value={formData.incorporation_year || ""} onChange={handleChange("incorporation_year")} testId="company-input-incorporation" />
        <FormField label="RC Number" type="text" value={formData.rc_number || ""} onChange={handleChange("rc_number")} testId="company-input-rc" />
      </div>
      <FormField label="Email" type="email" value={formData.email || ""} onChange={handleChange("email")} testId="company-input-email" />
      <button onClick={handleSave} disabled={saving} className="mt-6 font-['Barlow_Condensed'] text-sm font-bold tracking-[0.15em] uppercase text-[#0a0f1e] bg-[#00b4d8] px-6 py-3 hover:bg-white transition-colors disabled:opacity-50" data-testid="company-save-button">
        {saving ? "Saving..." : "Save Changes"}
      </button>
    </div>
  );
};

const ServicesManager = ({ services }) => (
  <div className="admin-card p-6" data-testid="services-manager">
    <h2 className="font-['Bebas_Neue'] text-2xl text-white mb-6">Manage Services ({services.length})</h2>
    <div className="space-y-4">
      {services.map((service, idx) => (
        <div key={service.id} className="p-4 border border-[#1e293b] bg-[#0a0f1e]" data-testid={`admin-service-${idx}`}>
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-semibold text-white">{service.title}</h3>
              <p className="text-sm text-[#94a3b8] mt-1">{service.description}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                {service.items.map((item) => <span key={item} className="text-xs bg-[rgba(0,180,216,0.15)] text-[#00b4d8] px-2 py-1">{item}</span>)}
              </div>
            </div>
            <span className="text-xs text-[#94a3b8]">#{idx + 1}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ProjectsManager = ({ projects }) => (
  <div className="admin-card p-6" data-testid="projects-manager">
    <h2 className="font-['Bebas_Neue'] text-2xl text-white mb-6">Manage Projects ({projects.length})</h2>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#1e293b]">
            <th className="text-left p-2 text-xs text-[#00b4d8] uppercase tracking-wider">Title</th>
            <th className="text-left p-2 text-xs text-[#00b4d8] uppercase tracking-wider">Client</th>
            <th className="text-left p-2 text-xs text-[#00b4d8] uppercase tracking-wider">Year</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project, idx) => (
            <tr key={project.id} className="border-b border-[#1e293b]/50" data-testid={`admin-project-${idx}`}>
              <td className="p-2 text-sm text-white">{project.title}</td>
              <td className="p-2 text-sm text-[#94a3b8]">{project.client}</td>
              <td className="p-2 text-sm text-[#94a3b8]">{project.date_awarded}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const GalleryManager = ({ gallery }) => (
  <div className="admin-card p-6" data-testid="gallery-manager">
    <h2 className="font-['Bebas_Neue'] text-2xl text-white mb-6">Manage Gallery ({gallery.length})</h2>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {gallery.map((image, idx) => (
        <div key={image.id} className="relative group" data-testid={`admin-gallery-${idx}`}>
          <img src={image.image_url} alt={image.title} className="w-full aspect-square object-cover border border-[#1e293b]" />
          <div className="absolute inset-0 bg-[#0a0f1e]/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <p className="text-xs text-white text-center px-2">{image.title}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const MessagesManager = ({ messages, onUpdate }) => {
  const markAsRead = (id) => {
    if (onUpdate) onUpdate(id);
  };

  return (
    <div className="admin-card p-6" data-testid="messages-manager">
      <h2 className="font-['Bebas_Neue'] text-2xl text-white mb-6">Messages ({messages.length})</h2>
      {messages.length === 0 ? (
        <p className="text-[#94a3b8]">No messages yet.</p>
      ) : (
        <div className="space-y-4">
          {messages.map((msg, idx) => (
            <div key={msg.id} className={`p-4 border ${msg.read ? "border-[#1e293b]" : "border-[#00b4d8]"} bg-[#0a0f1e]`} data-testid={`admin-message-${idx}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-white">{msg.name}</h3>
                  <p className="text-xs text-[#94a3b8]">{msg.email}</p>
                </div>
                {!msg.read && (
                  <button onClick={() => markAsRead(msg.id)} className="text-xs text-[#00b4d8] hover:underline" data-testid={`mark-read-${idx}`}>Mark as Read</button>
                )}
              </div>
              <p className="text-sm font-medium text-[#00b4d8] mb-1">{msg.subject}</p>
              <p className="text-sm text-[#94a3b8]">{msg.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [company, setCompany] = useState(staticCompany);
  const services = staticServices;
  const projects = staticProjects;
  const gallery = staticGallery;
  const [messages, setMessages] = useState([]);

  const updateCompany = (next) => setCompany(next);
  const markMessageRead = (id) =>
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "company", label: "Company Info", icon: Building2 },
    { id: "services", label: "Services", icon: Settings },
    { id: "projects", label: "Projects", icon: FileText },
    { id: "gallery", label: "Gallery", icon: ImageIcon },
    { id: "messages", label: "Messages", icon: MessageSquare }
  ];

  const unreadMessages = messages.filter(m => !m.read).length;

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-overview">
            <AdminStatCard icon={Settings} label="Services" value={services.length} idx={0} />
            <AdminStatCard icon={FileText} label="Projects" value={projects.length} idx={1} />
            <AdminStatCard icon={ImageIcon} label="Gallery Images" value={gallery.length} idx={2} />
            <AdminStatCard icon={MessageSquare} label="Unread Messages" value={unreadMessages} idx={3} />
          </div>
        );
      case "company": return <CompanyEditor company={company} onUpdate={updateCompany} />;
      case "services": return <ServicesManager services={services} />;
      case "projects": return <ProjectsManager projects={projects} />;
      case "gallery": return <GalleryManager gallery={gallery} />;
      case "messages": return <MessagesManager messages={messages} onUpdate={markMessageRead} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e]">
      <Navigation isAdmin />
      <div className="pt-20 px-4 md:px-8 pb-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-['Bebas_Neue'] text-4xl text-white mb-2" data-testid="admin-title">Admin Dashboard</h1>
          <p className="text-[#94a3b8] mb-8">Manage your company profile and content</p>
          <div className="flex flex-wrap gap-2 mb-8 border-b border-[#1e293b] pb-4">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 font-['Barlow_Condensed'] text-sm uppercase tracking-wider transition-colors ${activeTab === tab.id ? "bg-[#00b4d8] text-[#0a0f1e]" : "text-[#94a3b8] hover:text-[#00b4d8] border border-[#1e293b]"}`} data-testid={`admin-tab-${tab.id}`}>
                <tab.icon size={16} />{tab.label}
                {tab.id === "messages" && unreadMessages > 0 && <span className="bg-[#c9a84c] text-[#0a0f1e] text-xs px-2 rounded-full">{unreadMessages}</span>}
              </button>
            ))}
          </div>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

const ADMIN_PASSWORD = "Chris@1234";

const AdminLogin = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem("choze_admin_auth", "1");
      onSuccess();
    } else {
      setError("Incorrect password");
      setPassword("");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center px-4">
      <form onSubmit={handleSubmit} className="w-full max-w-md bg-[#12182b] border border-[#1e293b] p-8 relative" data-testid="admin-login">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#00b4d8] to-transparent" />
        <h1 className="font-['Bebas_Neue'] text-3xl text-white tracking-wide mb-2">Admin Portal</h1>
        <p className="text-sm text-[#94a3b8] mb-6">Enter the password to continue.</p>
        <label className="block text-xs tracking-[0.2em] uppercase text-[#00b4d8] font-semibold mb-2">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(""); }}
          className="w-full bg-[#0a0f1e] border border-[#1e293b] px-4 py-3 text-white focus:border-[#00b4d8] focus:outline-none mb-4"
          autoFocus
          data-testid="admin-password-input"
        />
        {error && <p className="text-sm text-[#ef4444] mb-4" data-testid="admin-login-error">{error}</p>}
        <button type="submit" className="w-full bg-[#00b4d8] text-[#0a0f1e] font-['Barlow_Condensed'] text-sm font-bold tracking-[0.15em] uppercase px-5 py-3 hover:bg-white transition-colors" data-testid="admin-login-button">
          Sign In
        </button>
        <Link to="/" className="block text-center mt-4 text-xs text-[#94a3b8] hover:text-[#00b4d8] transition-colors">Back to site</Link>
      </form>
    </div>
  );
};

const ProtectedAdmin = () => {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem("choze_admin_auth") === "1");
  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;
  return <AdminDashboard />;
};

function App() {
  return (
    <div className="App">
      <HashRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<ProtectedAdmin />} />
        </Routes>
      </HashRouter>
    </div>
  );
}

export default App;
